<template>
  <div id="jcom-table-preview">
    <v-card class="rounded-lg elevation-4 preview-card">
      <!-- Header AppBar -->
      <v-app-bar
        color="primary"
        dark
        elevate-on-scroll
        class="preview-header"
        scroll-target="#scrolling-techniques-7"
      >
        <v-icon class="mr-2">mdi-account-badge</v-icon>
        <v-toolbar-title class="font-weight-medium">{{
          PageTitle
        }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon dark @click="closePrompt">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-app-bar>

      <!-- Main Content -->
      <v-container style="overflow: hidden" class="pa-4">
        <!-- Loading Spinner -->
        <div class="d-flex justify-center mb-4" v-if="LoadingFlag">
          <v-progress-circular
            indeterminate
            color="primary"
            size="50"
          ></v-progress-circular>
        </div>

        <v-card-text class="pa-0">
          <div class="text-caption text-right mb-3">
            <span class="text-danger font-weight-medium">*</span> indicates
            required field
          </div>

          <v-form
            ref="form1"
            v-model="valid1"
            lazy-validation
            v-on:submit.prevent="confirmAlert"
          >
            <v-container class="pa-0">
              <!-- Member Profile Card -->
              <v-card v-if="MemberId > 0" class="mb-6 member-profile-card">
                <v-card-title class="primary white--text py-2 px-4">
                  <v-icon left color="white">mdi-account-circle</v-icon>
                  Member Information
                </v-card-title>
                <v-card-text class="pa-4">
                  <!-- Member Photo -->
                  <div class="text-center mb-4">
                    <v-avatar size="120" class="elevation-3">
                      <img :src="MemberImage" alt="Member Photo" />
                    </v-avatar>
                    <h3 class="mt-2 mb-0">{{ MemberName }}</h3>
                    <div class="text-subtitle-1 grey--Text text--darken-1">
                      {{ MembershipId }}
                    </div>
                  </div>
                </v-card-text>
                <v-row class="mb-6" align="stretch" dense>
                  <!-- Left: Portal Details -->
                  <v-col cols="12" md="6">
                    <v-card class="portal-details-card h-100">
                      <v-card-title class="primary white--text py-2 px-4">
                        <v-icon left color="white">mdi-web</v-icon>
                        Portal Details
                      </v-card-title>
                      <v-card-text class="pa-4">
                        <div class="portal-details-grid">
                          <!-- Row 1 -->
                          <div class="portal-detail-item">
                            <div class="portal-label">Active Status</div>
                            <div class="portal-value">
                              <v-chip
                                :color="getActiveStatusColor(StatusTxt)"
                                small
                                dark
                                >{{ StatusTxt }}</v-chip
                              >
                            </div>
                          </div>

                          <div class="portal-detail-item">
                            <div class="portal-label">Zone</div>
                            <div class="portal-value">{{ ZoneName }}</div>
                          </div>

                          <div class="portal-detail-item">
                            <div class="portal-label">Email</div>
                            <div class="portal-value">{{ EmailId }}</div>
                          </div>

                          <!-- Row 2 -->
                          <div class="portal-detail-item">
                            <div class="portal-label">Local Organization</div>
                            <div class="portal-value">{{ LomName }}</div>
                          </div>

                          <div class="portal-detail-item">
                            <div class="portal-label">Designation</div>
                            <div class="portal-value">
                              {{ MemberDesignation }}
                            </div>
                          </div>

                          <div class="portal-detail-item">
                            <div class="portal-label">Date of Birth</div>
                            <div class="portal-value">{{ DOB }}</div>
                          </div>

                          <!-- Row 3 -->
                          <div class="portal-detail-item">
                            <div class="portal-label">Contact</div>
                            <div class="portal-value">{{ MobileNo }}</div>
                          </div>

                          <div class="portal-detail-item">
                            <div class="portal-label">Profile Update</div>
                            <div class="portal-value">
                              <v-chip
                                small
                                :color="recordData.ProfileUpdatedColor"
                                dark
                                >{{ recordData.ProfileUpdatedTxt }}</v-chip
                              >
                            </div>
                          </div>

                          <div class="portal-detail-item">
                            <div class="portal-label">Aadhaar Verification</div>
                            <div class="portal-value">
                              <v-chip
                                small
                                :color="recordData.AadhaarVerifiedColor"
                                dark
                                >{{ recordData.AadhaarVerifiedTxt }}</v-chip
                              >
                            </div>
                          </div>

                          <!-- Missing Steps -->
                          <div 
                            v-if="getMissingStepsArray().length > 0"
                            class="portal-detail-item"
                          >
                            <div class="portal-label">Missing Steps</div>
                            <v-tooltip 
                              top 
                              :disabled="getMissingStepsArray().length === 0"
                            >
                              <template v-slot:activator="{ on, attrs }">
                                <div 
                                  class="portal-value status-warning missing-steps-inline"
                                  v-bind="attrs"
                                  v-on="on"
                                >
                                  <v-icon x-small left>mdi-alert-outline</v-icon>
                                  <span class="missing-steps-text">{{ getMissingStepsArray().length }} Pending</span>
                                </div>
                              </template>
                              <div class="tooltip-content">
                                <div v-for="(step, idx) in getMissingStepsArray()" :key="idx" class="tooltip-item">
                                  • {{ step.trim() }}
                                </div>
                              </div>
                            </v-tooltip>
                          </div>
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>

                  <!-- Right: Aadhaar Card (show only if verified) -->
                  <v-col cols="12" md="6">
                    <v-card
                      class="aadhaar-card-container elevation-3 h-100"
                      outlined
                    >
                      <v-card-title class="green white--text py-2 px-4">
                        <v-icon left color="white">mdi-account-badge</v-icon>
                        Verified Aadhaar Details
                      </v-card-title>

                      <v-card-text class="pa-0">
                        <!-- If Aadhaar verified -> show full aadhaar markup -->
                        <template
                          v-if="recordData && recordData.AadhaarVerified"
                        >
                          <v-card
                            elevation="0"
                            class="aadhaar-real-card mx-auto"
                            rounded="lg"
                            max-width="100%"
                          >
                            <div class="aadhaar-outer-wrapper">
                              <!-- existing aadhaar header / flag / content -->
                              <v-card-text class="pa-4">
                                <div class="d-flex aadhaar-content">
                                  <div class="aadhaar-photo-section mr-4">
                                    <v-avatar
                                      size="90"
                                      class="aadhaar-photo-container mb-3"
                                    >
                                      <img
                                        v-if="recordData.PhotoUrl"
                                        :src="recordData.PhotoUrl"
                                        alt="Aadhaar Photo"
                                        class="aadhaar-photo"
                                      />
                                      <v-icon v-else size="50" color="#555555"
                                        >mdi-account</v-icon
                                      >
                                    </v-avatar>
                                    <div class="qr-code-container mx-auto">
                                      <v-img
                                        src="data:image/png;base64,iVBORw0K..."
                                        width="50"
                                        height="50"
                                        contain
                                      ></v-img>
                                    </div>
                                  </div>
                                  <div
                                    class="aadhaar-details-section flex-grow-1"
                                  >
                                    <div class="person-name-bilingual">
                                      <div class="name-hindi mb-1">
                                        {{ recordData.AadhaarName || "NA" }}
                                      </div>
                                      <div class="name-english">
                                        {{ recordData.AadhaarName || "NA" }}
                                      </div>
                                    </div>
                                    <div class="detail-row mt-2">
                                      <span class="detail-label"
                                        ><b>Date of Birth:</b></span
                                      >
                                      <span class="detail-value">{{
                                        recordData.AadhaarDob || "NA"
                                      }}</span>
                                    </div>
                                    <div class="detail-row">
                                      <span class="detail-label"
                                        ><b>Gender:</b></span
                                      >
                                      <span class="detail-value">{{
                                        recordData.AadhaarGender || "NA"
                                      }}</span>
                                    </div>
                                    <div>
                                      <span class="detail-label"
                                        ><b>Address:</b></span
                                      >
                                      <span class="detail-value address-text">{{
                                        recordData.AadhaarAddress || "NA"
                                      }}</span>
                                    </div>
                                    <div class="aadhaar-number-container mt-2">
                                      <div class="aadhaar-number-label">
                                        आधार / Aadhaar
                                      </div>
                                      <div class="aadhaar-number">
                                        <template
                                          v-if="recordData.AadhaarNumber"
                                        >
                                          <span class="aadhaar-digit-group">{{
                                            recordData.AadhaarNumber
                                          }}</span>
                                        </template>
                                        <template v-else
                                          >XXXX XXXX XXXX</template
                                        >
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div class="aadhaar-separator-line mt-3"></div>
                                <div
                                  class="aadhaar-footer d-flex justify-space-between align-center mt-1"
                                >
                                  <div class="aadhaar-issue-info caption">
                                    <div class="issue-date">
                                      Issued:
                                      {{
                                        recordData.AadhaarIssueDate ||
                                        "01/01/2023"
                                      }}
                                    </div>
                                  </div>
                                  <div class="aadhaar-tagline">
                                    <span class="tagline-text-hindi"
                                      >आधार - आम आदमी का अधिकार</span
                                    >
                                  </div>
                                  <div class="verification-badge">
                                    <v-chip
                                      color="success"
                                      x-small
                                      dark
                                      class="verification-chip"
                                    >
                                      <v-icon left x-small
                                        >mdi-check-circle</v-icon
                                      >
                                      Verified
                                    </v-chip>
                                  </div>
                                </div>
                              </v-card-text>
                            </div>
                          </v-card>
                        </template>

                        <!-- Else -> show friendly error/message inside same card -->
                        <template v-else>
                          <v-card-text class="pa-6 text-center">
                            <v-icon color="error" x-large class="mb-2"
                              >mdi-alert-circle-outline</v-icon
                            >
                            <h3 class="mt-2 mb-1">Aadhaar details not found</h3>
                            <div class="caption grey--text">
                              No Aadhaar record is available for this member.
                              Please request Aadhaar verification or upload
                              Aadhaar document.
                            </div>
                          </v-card-text>
                        </template>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </v-card>

              <!-- Age Proof Document Section -->
              <v-card class="mb-6 document-card">
                <v-card-title class="primary white--text py-2 px-4">
                  <v-icon left color="white">mdi-file-document</v-icon>
                  Age Proof Document
                </v-card-title>
                <v-card-text class="pa-4">
                  <v-row align="center">
                    <v-col cols="12" sm="3" md="3">
                      <v-chip
                        :color="`${DocumentUpdate ? '#0f9d58' : '#f4b400'}`"
                        label
                        dark
                        class="px-3"
                      >
                        <v-icon left small>{{
                          DocumentUpdate
                            ? "mdi-check-circle"
                            : "mdi-clock-outline"
                        }}</v-icon>
                        {{ DocumentUpdate ? "Completed" : "Pending" }}
                      </v-chip>
                    </v-col>
                    <v-col cols="12" sm="9" md="9">
                      <div class="document-images-container">
                        <viewer rebuild :images="DocumentImagesArray">
                          <div class="d-flex flex-wrap">
                            <div
                              class="document-thumbnail ma-1"
                              v-for="(src, i) in DocumentImagesArray"
                              :key="i"
                            >
                              <img
                                width="80"
                                height="80"
                                :src="src"
                                class="thumbnail-image"
                              />
                            </div>
                          </div>
                        </viewer>
                      </div>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>

              <!-- Directory Information Document Section -->
              <v-card class="mb-6 document-section-card">
                <v-card-title class="success white--text py-2 px-4">
                  <v-icon left color="white">mdi-file-document-multiple</v-icon>
                  Directory Information Document
                </v-card-title>
                <v-card-text class="pa-4">
                  <div
                    v-if="LomDocumentPathArray.length > 0"
                    class="document-links"
                  >
                    <v-list dense class="pa-0 document-list">
                      <v-list-item
                        v-for="(row, index) in LomDocumentPathArray"
                        :key="index"
                        class="document-list-item"
                      >
                        <v-list-item-icon class="mr-2">
                          <v-icon color="primary">mdi-file-pdf-box</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="document-link">
                            <a
                              :href="row"
                              target="_blank"
                              class="text-decoration-none"
                            >
                              Directory Information Document {{ index + 1 }}
                            </a>
                          </v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action>
                          <v-btn
                            icon
                            small
                            color="primary"
                            :href="row"
                            target="_blank"
                          >
                            <v-icon small>mdi-download</v-icon>
                          </v-btn>
                        </v-list-item-action>
                      </v-list-item>
                    </v-list>
                  </div>
                  <div
                    v-else
                    class="document-upload-prompt d-flex align-center"
                  >
                    <v-chip color="#f4b400" label dark class="mr-3">
                      <v-icon left small>mdi-alert-circle</v-icon>
                      Pending
                    </v-chip>
                  </div>
                  <br />
                  <v-btn
                    color="primary"
                    small
                    rounded
                    elevation="1"
                    @click.prevent="uploadDocumentPrompt = true"
                    class="upload-button"
                  >
                    <v-icon left small>mdi-cloud-upload</v-icon>
                    Upload Document
                  </v-btn>
                </v-card-text>
              </v-card>

              <!-- Reason History Section -->
              <v-card
                v-if="ReasonHistory !== ''"
                class="mb-6 reason-history-card"
              >
                <v-card-title class="warning white--text py-2 px-4">
                  <v-icon left color="white">mdi-history</v-icon>
                  Reason History
                </v-card-title>
                <v-card-text class="pa-4">
                  <div
                    class="reason-history-content"
                    v-html="ReasonHistory"
                  ></div>
                </v-card-text>
              </v-card>

              <!-- Reason Input -->
              <v-card class="mb-6 reason-input-card">
                <v-card-title
                  class="deep-orange lighten-1 white--text py-2 px-4"
                >
                  <v-icon left color="white">mdi-comment-text</v-icon>
                  <span>Reason</span>
                  <span class="text-danger ml-1">*</span>
                </v-card-title>
                <v-card-text class="pa-4">
                  <v-textarea
                    outlined
                    v-model="Reason"
                    :rules="ReasonRules"
                    label="Please provide your reason"
                    counter
                    rows="3"
                    auto-grow
                    hide-details="auto"
                    placeholder="Enter your reason here..."
                    class="reason-textarea"
                  ></v-textarea>
                </v-card-text>
              </v-card>

              <!-- Action Buttons -->
              <div class="text-center action-buttons mt-4">
                <v-btn
                  :disabled="!valid1"
                  @click.prevent="confirmAlert(1)"
                  color="success"
                  elevation="2"
                  x-large
                  rounded
                  class="action-button px-6"
                >
                  <v-icon left>mdi-check-circle</v-icon>
                  Approved
                </v-btn>

                <v-btn
                  :disabled="!valid1"
                  @click.prevent="confirmAlert(3)"
                  color="error"
                  elevation="2"
                  x-large
                  rounded
                  class="action-button px-6 mx-4"
                >
                  <v-icon left>mdi-close-circle</v-icon>
                  Keep Pending
                </v-btn>

                <v-btn
                  @click="closePrompt"
                  color="grey darken-1"
                  elevation="2"
                  x-large
                  rounded
                  class="action-button px-6 white--text"
                >
                  <v-icon left>mdi-keyboard-return</v-icon>
                  Cancel
                </v-btn>
              </div>
            </v-container>
          </v-form>
        </v-card-text>
      </v-container>
    </v-card>

    <!-- Document Upload Dialog -->
    <v-dialog v-model="uploadDocumentPrompt" persistent max-width="70%">
      <add-lom-document
        pageTitle="Upload Directory Information"
        pageDescription="Upload Directory Information"
        :recordData="recordData"
        :addRecordPrompt="uploadDocumentPrompt"
        @hideAddRecordPrompt="hideUploadRecordPrompt"
        v-if="uploadDocumentPrompt"
        :UserInterface="2"
      ></add-lom-document>
    </v-dialog>
  </div>
</template>
<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import Swal from "sweetalert2";
import { CircleStencil, Cropper } from "vue-advanced-cropper";
import "vue-advanced-cropper/dist/style.css";
import AddLomDocument from "@/view/pages/erp/reports/AddLomDocument.vue";

export default {
  mixins: [common],
  components: {
    Cropper,
    CircleStencil,
    AddLomDocument,
  },
  props: {
    previewRecordPrompt: {
      type: Boolean,
      required: true,
    },
    recordData: {
      required: true,
    },
  },
  data() {
    return {
      valid1: false,
      valid2: true,
      LoadingFlag: false,
      SubmitFlag: false,

      UploadFlag: false,
      ResultFlag: false,

      MemberId: "",
      MemberImage: "",
      MemberName: "",
      MembershipId: "",
      ZoneName: "",
      LomName: "",
      MemberDesignation: "",
      MobileNo: "",
      EmailId: "",
      Address: "",
      DOB: "",
      YearwiseOfficeHeldId: "",
      ProfileUpdate: "",
      DocumentUpdate: "",
      DocumentImage: "",
      DocumentImagesArray: [],
      LomDocumentPathArray: [],
      StatusTxt: "",
      ReasonHistory: "",
      MissingSteps: [],

      uploadDocumentPrompt: false,

      Reason: "",
      ReasonRules: [(v) => !!v || "Reason is required"],
      menu1: false,

      ActiveStatus: 1,

      PageInfo: {},
      PageTitle: "Directory Information Preview",
      PageDescription: "",
      CurrentYearId: 0,
      YearName: "",
      NextYearCode: "",
      NextYearName: "",
    };
  },
  computed: {},
  watch: {
    PageInfo: function () {
      console.log("watch PageInfo");
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;

        var CurrentJciYearId = parseInt(tr.CurrentJciYearId);
        console.log({ CurrentJciYearId });

        var YearName = parseInt(tr.CurrentJciYearName);
        console.log({ YearName });
        this.YearName = YearName;

        this.CurrentYearId = CurrentJciYearId;

        var todayDate = new Date().toISOString().slice(0, 10);
        console.log(todayDate);

        this.LaunchDate = todayDate;
        this.pageData();
      }
    },
  },
  mounted() {},
  methods: {
    refreshPageData() {
      console.log("refreshPageData called");
      this.LoadingFlag = true;
      var url1 = "api/menu-details/show";
      var condition1 = {
        UserInterface: 1,
        Module: "jcom_table",
        Action: "preview",
      };
      // this.getPageDetails("PageInfo", {}, url1, condition1);
      this.LoadingFlag = false;

      var recordData = this.recordData;
      console.log("recordData = " + JSON.stringify(recordData));
      this.getTableRecords();
    },
    closePrompt() {
      console.log("closePrompt called");
      console.log("ResultFlag = " + this.ResultFlag);
      this.$emit("hidePreviewRecordPrompt", this.ResultFlag);
    },
    hideUploadRecordPrompt(flag) {
      console.log("hideUploadRecordPrompt called");
      this.uploadDocumentPrompt = false;
      if (flag) {
        this.$emit("hidePreviewRecordPrompt", true);
      }
    },
    getTableRecords() {
      console.log("getTableRecords called");

      this.MemberId = this.recordData.MemberId;
      this.MemberImage = this.recordData.MemberImagePath;
      this.MemberName = this.recordData.MemberName;
      this.MembershipId = this.recordData.MembershipId;
      this.ZoneName = this.recordData.Zone;
      this.LomName = this.recordData.LOM;
      this.MemberDesignation = this.recordData.Designaton;
      this.MobileNo = this.recordData.Mobile;
      this.EmailId = this.recordData.Email;
      this.Address = this.recordData.Address;
      this.DOB = this.recordData.DOB;
      this.YearwiseOfficeHeldId = this.recordData.YearwiseOfficeHeldId;
      this.LomDocumentPathArray = this.recordData.LomDocumentPathArray;
      this.ProfileUpdate = this.recordData.MemberProfileUpdated;
      this.StatusTxt = this.recordData.YearwiseOfficeHeldStatusTxt;
      this.ReasonHistory = this.recordData.Reason;
      this.MissingSteps = this.recordData.MissingSteps || [];

      var server_url = companyConfig.apiURL;
      var add_url = server_url + "assets/images/white-user.png";
      console.log({ add_url });
      this.MemberImage = this.MemberImage == "" ? add_url : this.MemberImage;
      console.log("member image" + this.MemberImage);
      this.DocumentUpdate = this.recordData.MemberDocumentFlag;
      this.DocumentImage = this.recordData.MemberDocumentPath;
      console.log("DocumentImage" + this.DocumentImage);
      this.DocumentImagesArray = this.recordData.MemberDocumentPathArray;
    },
    getMissingStepsArray() {
      if (Array.isArray(this.MissingSteps)) {
        return this.MissingSteps;
      } else if (typeof this.MissingSteps === 'string' && this.MissingSteps.trim()) {
        return this.MissingSteps.split(',').map(step => step.trim());
      }
      return [];
    },
    confirmAlert(status) {
      console.log({ status });
      var validate1 = this.$refs.form1.validate();
      console.log({ validate1 });

      if (validate1) {
        Swal.fire({
          title: "Do you want to continue?",
          text: "Please verify all the information before proceeding.",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: `Continue`,
          confirmButtonColor: "#3085d6",
          cancelButtonText: `Cancel`,
          cancelButtonColor: "#d33",
        }).then((result) => {
          /* Read more about isConfirmed, isDenied below */
          if (result.isConfirmed) {
            this.updateActiveStatus(status);
          } else {
            this.sweetAlert("error", "You cancelled the process", true);
          }
        });
      } else {
        var message = "Kindly fill the required fields";
        this.sweetAlert("error", message);
      }
    },
    updateActiveStatus(status) {
      console.log("updateActiveStatus is called");

      var validate1 = this.$refs.form1.validate();
      console.log({ validate1 });

      var Status = status;
      var Reason = this.Reason;
      var YearwiseOfficeHeldId = this.YearwiseOfficeHeldId;
      console.log({ Status }, { Reason }, { YearwiseOfficeHeldId });

      if (validate1) {
        this.LoadingFlag = true;
        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/year-wise-designation/status-update";
        var upload = {
          UserInterface: 1,
          Status: Status,
          Reason: Reason,
          YearwiseOfficeHeld: YearwiseOfficeHeldId,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = "";
        var successTxt = "";
        var errorTxt = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));
            thisIns.LoadingFlag = false;

            flag = response.data.flag;
            output = response.data.output;
            records = response.data.records;
            console.log("output=" + output + ", flag=" + flag);
            successTxt = response.data.success;
            errorTxt = response.data.error;
            console.log("successTxt=" + successTxt + ", errorTxt=" + errorTxt);

            if (flag == 1) {
              thisIns.sweetAlert("success", output, true);
              thisIns.ResultFlag = true;
              thisIns.closePrompt();
            } else {
              thisIns.sweetAlert("error", output, true);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.LoadingFlag = false;
          });
      } else {
        var message = "Kindly fill the required fields";
        this.sweetAlert("error", message);
      }
    },
  },

  beforeMount() {
    this.refreshPageData();
  },
};
</script>
    
<style lang="scss">
#jcom-table-preview {
  .preview-card {
    border-radius: 12px;
    overflow: hidden;
  }

  .preview-header {
    border-top-left-radius: 12px;
    border-top-right-radius: 12px;
  }

  .text-danger {
    color: #f44336 !important;
  }

  .member-profile-card {
    transition: box-shadow 0.3s;
    border-radius: 8px;
    overflow: hidden;

    &:hover {
      box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
    }
  }

  .member-detail-col {
    .detail-label {
      font-size: 12px;
      color: rgba(0, 0, 0, 0.6);
      font-weight: 500;
      margin-bottom: 4px;
    }

    .detail-value {
      font-size: 14px;
      font-weight: 500;
    }
  }

  /* NEW: Portal Details Card */
  .portal-details-card {
    border-radius: 8px;
    overflow: hidden;
    transition: box-shadow 0.3s;
    &:hover {
      box-shadow: 0 6px 18px rgba(0, 0, 0, 0.06);
    }
  }

  // Portal Details Grid Layout
  .portal-details-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 20px 24px;
    align-items: start;
  }

  .portal-detail-item {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .portal-detail-item .portal-label {
    font-size: 11px;
    color: #37474f;
    font-weight: 700;
    margin-bottom: 4px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .portal-detail-item .portal-value {
    font-size: 14px;
    color: #1f2937;
    background: transparent;
    display: flex;
    align-items: center;
    gap: 6px;
    line-height: 1.3;

    &.status-warning {
      color: #f59e0b;
      font-weight: 600;
    }

    &.missing-steps-inline {
      display: flex;
      align-items: center;
      cursor: pointer;
      white-space: nowrap;
      transition: all 0.2s ease;
      padding: 2px 6px;
      border-radius: 4px;

      &:hover {
        background-color: rgba(255, 193, 7, 0.1);
      }

      .missing-steps-text {
        user-select: none;
        font-weight: 500;
        margin-left: 4px;
      }
    }
  }

  .tooltip-content {
    max-width: 320px;
    padding: 12px 8px;
    text-align: left;

    .tooltip-item {
      padding: 4px 0;
      font-size: 12px;
      line-height: 1.5;
      word-break: break-word;
      color: rgba(255, 255, 255, 0.95);
    }
  }

  /* NEW: Aadhaar Summary Card */
  .aadhar-summary-card {
    border-radius: 8px;
    overflow: hidden;
    transition: box-shadow 0.3s;
    .summary-label {
      font-size: 12px;
      color: rgba(0, 0, 0, 0.6);
      font-weight: 600;
      margin-bottom: 6px;
    }
    .summary-value {
      font-size: 14px;
      color: #263238;
      background: #f8fafc;
      padding: 8px 10px;
      border-radius: 6px;
      border-left: 3px solid #1976d2;
    }
    .aadhaar-number {
      font-family: "Courier New", monospace;
      font-weight: 700;
      letter-spacing: 1px;
    }
    &:hover {
      box-shadow: 0 6px 18px rgba(0, 0, 0, 0.06);
    }
  }

  .document-card,
  .document-section-card,
  .reason-history-card,
  .reason-input-card,
  .aadhaar-card-container {
    border-radius: 8px;
    overflow: hidden;
    transition: box-shadow 0.3s;

    &:hover {
      box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
    }
  }

  .document-thumbnail {
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    overflow: hidden;
    transition: transform 0.2s;

    &:hover {
      transform: scale(1.05);
      box-shadow: 0 3px 8px rgba(0, 0, 0, 0.1);
    }

    .thumbnail-image {
      object-fit: cover;
      border-radius: 4px;
    }
  }

  .document-links {
    .document-list {
      background: transparent;

      .document-list-item {
        border-radius: 4px;
        margin-bottom: 4px;
        transition: background-color 0.2s;

        &:hover {
          background-color: rgba(0, 0, 0, 0.03);
        }

        .document-link {
          a {
            color: #1976d2;

            &:hover {
              text-decoration: underline !important;
            }
          }
        }
      }
    }
  }

  .document-upload-prompt {
    .upload-button {
      transition: transform 0.2s;

      &:hover {
        transform: scale(1.05);
      }
    }
  }

  .reason-history-content {
    background-color: rgba(0, 0, 0, 0.02);
    padding: 12px;
    border-radius: 4px;
    border-left: 4px solid #ffc107;
  }

  .reason-textarea {
    background-color: #fff;
  }

  .action-buttons {
    margin-bottom: 24px;

    .action-button {
      transition: transform 0.2s, box-shadow 0.2s;
      min-width: 140px;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
      }
    }
  }

  /* Aadhaar Card Styles */
  .aadhaar-real-card {
    background: white !important;
    overflow: hidden;

    .aadhaar-header {
      background-color: #f8f8f8;
      padding: 8px;
    }

    .aadhaar-title-container .govt-title-hindi {
      color: black !important;
      font-size: 14px !important;
      display: block !important;
      visibility: visible !important;
      opacity: 1 !important;
    }

    .aadhaar-title-container .govt-title-english {
      color: #333 !important;
      font-size: 12px !important;
      display: block !important;
      visibility: visible !important;
      opacity: 1 !important;
    }

    .govt-title-hindi {
      font-size: 14px;
      font-weight: bold;
      color: #000 !important;
      margin: 0;
      line-height: 1.2;
    }

    .govt-title-english {
      font-size: 12px;
      font-weight: 600;
      color: #333 !important;
      margin: 0;
      line-height: 1.2;
    }

    .aadhaar-flag-colors {
      display: flex;
      flex-direction: column;
      height: 6px;
      width: 100%;

      .saffron-line {
        height: 2px;
        background: #ff9933;
      }

      .white-line {
        height: 2px;
        background: #ffffff;
        position: relative;

        .ashoka-chakra {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        }
      }

      .green-line {
        height: 2px;
        background: #138808;
      }
    }

    .aadhaar-logo {
      font-size: 14px;
    }

    .aadhaar-content {
      display: flex;
      align-items: flex-start;
    }

    .aadhaar-photo-container {
      border: 1px solid #ccc;
      background: #f5f5f5;
    }

    .qr-code-container {
      border: 1px dashed #ccc;
      padding: 2px;
      background: #f9f9f9;
      width: fit-content;
    }

    .aadhaar-details-section {
      flex: 1;
    }

    .person-name-bilingual {
      .name-hindi {
        font-size: 12px;
        color: #555;
      }

      .name-english {
        font-size: 14px;
        font-weight: bold;
        color: #333;
      }
    }

    .detail-row {
      margin-bottom: 4px;

      .detail-label {
        display: inline-block;
        min-width: 90px;
        font-weight: 600;
        font-size: 12px;
        color: #555;
      }

      .detail-value {
        font-size: 12px;
        color: #333;
      }

      .address-text {
        max-width: 200px;
        display: inline-block;
        word-break: break-word;
      }
    }

    .aadhaar-number-container {
      background: #f5f5f5;
      border: 1px solid #ddd;
      border-radius: 4px;
      padding: 4px 8px;
      display: inline-block;
      max-width: 200px;

      .aadhaar-number-label {
        font-size: 9px;
        color: #666;
        margin-bottom: 2px;
      }

      .aadhaar-number {
        font-weight: bold;
        letter-spacing: 1px;
        color: #000;
        font-size: 14px;
        font-family: "Courier New", monospace;
      }
    }

    .aadhaar-separator-line {
      height: 2px;
      background: linear-gradient(
        to right,
        #f00,
        #f00 75%,
        transparent 75%,
        transparent 100%
      );
      background-size: 10px 1px;
      background-repeat: repeat-x;
    }

    .aadhaar-footer {
      .issue-date {
        font-size: 8px;
        color: #666;
      }

      .tagline-text-hindi {
        font-size: 10px;
        color: #d32f2f;
        font-weight: 500;
      }
    }
  }
}
</style>